/**
 * Iubenda consent helpers.
 *
 * Iubenda manages cookie consent (and Google Consent Mode v2) at the site
 * level. For third-party tags that are NOT covered by Google Consent Mode
 * (e.g. the Facebook/Meta Pixel) we must gate their initialisation on the
 * user's consent ourselves, mirroring how the marketing website gates PostHog.
 *
 * `IubendaProvider` bridges Iubenda's consent lifecycle into a single
 * `iubenda:consent` CustomEvent whose `detail.purposes` follows the Iubenda
 * standard (e.g. `purposes[5]` = Targeting & Advertising). These helpers read
 * and subscribe to that consent in a way decoupled from the Iubenda API.
 *
 * Purpose -> Google signal mapping (validated empirically against the live
 * Iubenda config, site 3424432):
 * - purpose 4 (Measurement)             -> analytics_storage
 * - purpose 5 (Targeting & Advertising) -> ad_storage, ad_user_data, ad_personalization
 */
export var IUBENDA_PURPOSE = {
  /** Strictly necessary. Always granted. */
  STRICTLY_NECESSARY: 1,

  /** Basic interactions & functionalities. */
  BASIC_INTERACTIONS: 2,

  /** Experience enhancement. */
  EXPERIENCE: 3,

  /** Measurement / analytics -> Google `analytics_storage`. */
  MEASUREMENT: 4,

  /** Targeting & Advertising -> Google `ad_storage` / `ad_user_data` / `ad_personalization`. */
  ADVERTISING: 5
};

/**
 * Synchronously read the current Iubenda consent for a purpose.
 * Returns `false` when Iubenda has not loaded yet or no consent was expressed.
 */
export function hasIubendaConsent(purposeId) {
  var _window, _window$_iub, _window$_iub$cs, _window$_iub$cs$conse;

  if (false) return false;
  var purposes = (_window = window) === null || _window === void 0 ? void 0 : (_window$_iub = _window._iub) === null || _window$_iub === void 0 ? void 0 : (_window$_iub$cs = _window$_iub.cs) === null || _window$_iub$cs === void 0 ? void 0 : (_window$_iub$cs$conse = _window$_iub$cs.consent) === null || _window$_iub$cs$conse === void 0 ? void 0 : _window$_iub$cs$conse.purposes;
  return (purposes === null || purposes === void 0 ? void 0 : purposes[purposeId]) === true;
}
/**
 * Subscribe to Iubenda consent changes for a single purpose.
 *
 * Invokes `callback` with the granted state every time the user expresses or
 * updates consent (via the `iubenda:consent` bridge event), and once
 * synchronously if consent for the purpose is already stored at call time
 * (covers returning users whose preference is read on page load).
 *
 * Returns an unsubscribe function.
 */

export function onIubendaConsent(purposeId, callback) {
  if (false) return function () {
    return undefined;
  };

  var handler = function handler(event) {
    var _detail;

    var purposes = ((_detail = event.detail) === null || _detail === void 0 ? void 0 : _detail.purposes) || {};
    callback(purposes[purposeId] === true);
  };

  window.addEventListener('iubenda:consent', handler); // Fire once for already-stored consent (returning user, preference read on load).

  if (hasIubendaConsent(purposeId)) callback(true);
  return function () {
    return window.removeEventListener('iubenda:consent', handler);
  };
}